import React, { useEffect, useState } from 'react';

import { Button, Modal } from '@copilot/ui';
import Icon from '../common/Icon';
import { useDesktopRelease } from '../../hooks/useDesktopRelease';
import { detectOlderWindows10 } from '../../utils/windowsVersion';

// Shown under every Windows download, for browsers that cannot report the
// Windows version (Firefox, Safari).
const OLDER_WINDOWS_NOTE =
  'Windows 10 before version 2004: share a window or a tab in your meeting, not the entire screen.';

/** true / false, or null while unknown (see utils/windowsVersion.js). */
function useOlderWindows10() {
  const [older, setOlder] = useState(null);
  useEffect(() => {
    let cancelled = false;
    detectOlderWindows10().then((value) => {
      if (!cancelled) setOlder(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return older;
}

/**
 * The download choices themselves - promoted button for the detected
 * platform, the other builds beneath it, and the unsigned-app warning.
 *
 * Rendered by BOTH the public /download page and the in-app modal, so
 * the two can never drift into offering different things.
 *
 * Every interactive element here carries its own colours (see
 * `.dl-*` in layout.css) rather than reusing `.btn-primary`. These are
 * anchors, and an anchor picks up a colour from the global `a` rule in
 * tokens.css; relying on a button class to win that fight is how the
 * first version of this page shipped with an unreadable label.
 */
function DesktopDownload({ compact = false, presentation = 'default' }) {
  const { version, failed, loading, primary, primaryUrl, others, releasesPage } =
    useDesktopRelease();
  const platforms = [{ ...primary, url: primaryUrl }, ...others];
  const windows = platforms.find((platform) => platform.id === 'windows');
  const mac = platforms.find((platform) => platform.id === 'macos');
  const ubuntu = platforms.find((platform) => platform.id === 'linux');
  const macUrl = mac?.url || releasesPage;
  const windowsUrl = windows?.url || releasesPage;

  // Older Windows 10: the installer download waits until the person has
  // read how screen sharing works on their version.
  const olderWindows10 = useOlderWindows10();
  const [pendingWindowsUrl, setPendingWindowsUrl] = useState(null);
  const warnBeforeWindowsDownload = (url) => (event) => {
    if (olderWindows10 !== true || !url || url === releasesPage) return;
    event.preventDefault();
    setPendingWindowsUrl(url);
  };
  const olderWindowsWarning = pendingWindowsUrl && (
    <OlderWindowsWarning url={pendingWindowsUrl} onClose={() => setPendingWindowsUrl(null)} />
  );

  if (failed) {
    return (
      <div className="dl-fallback">
        <p>We couldn&apos;t load the latest version automatically.</p>
        <a className="dl-primary" href={releasesPage} rel="noreferrer">
          <Icon name="download" size={18} />
          <span>View all downloads</span>
        </a>
      </div>
    );
  }

  if (presentation === 'landing') {
    return (
      <div className="dl dl-landing">
        <p className="dl-landing-kicker">DOWNLOAD</p>
        <h3>Ready for your next interview?</h3>
        <p className="dl-landing-copy">
          Download the desktop copilot for live interview and meeting assistance.
        </p>
        <p className="dl-landing-note">No credit card. No setup. Works instantly.</p>

        <div className="dl-landing-actions">
          {ubuntu?.url && (
            <div className="dl-landing-option">
              <a className="dl-landing-button dl-landing-button-secondary" href={ubuntu.url}>
                Download for Ubuntu
              </a>
              <Requirements platform={ubuntu} />
              <p className="dl-landing-req">Ubuntu beta: screenshots and hiding the panel from screen sharing are unavailable. Protected overlays and meetings are blocked.</p>
            </div>
          )}
          <div className="dl-landing-option">
            <a
              className={`dl-landing-button dl-landing-button-primary${loading ? ' is-loading' : ''}`}
              href={macUrl}
              aria-disabled={loading || undefined}
            >
              Download for Mac
            </a>
            <Requirements platform={mac} />
          </div>
          <div className="dl-landing-option">
            <a
              className={`dl-landing-button dl-landing-button-secondary${loading ? ' is-loading' : ''}`}
              href={windowsUrl}
              aria-disabled={loading || undefined}
              onClick={warnBeforeWindowsDownload(windowsUrl)}
            >
              Download for Windows
            </a>
            <Requirements platform={windows} />
            <p className="dl-landing-req">{OLDER_WINDOWS_NOTE}</p>
          </div>
        </div>
        {olderWindowsWarning}

        {primary.id === 'linux' && !loading && !primaryUrl && (
          <p className="dl-meta">The current release does not include an Ubuntu installer yet.</p>
        )}

        <p className="dl-meta dl-landing-meta">
          {version ? `Version ${version}` : 'Finding the latest version...'}
        </p>
      </div>
    );
  }

  return (
    <div className="dl">
      <a
        className={`dl-primary${loading ? ' is-loading' : ''}`}
        href={primaryUrl || releasesPage}
        aria-disabled={loading || undefined}
        onClick={primary.id === 'windows' ? warnBeforeWindowsDownload(primaryUrl) : undefined}
      >
        <Icon name={primary.icon} size={18} />
        <span>{primary.id === 'linux' && !loading && !primaryUrl ? 'View available downloads' : `Download for ${primary.label}`}</span>
      </a>

      <p className="dl-meta">
        {version ? `Version ${version}` : 'Finding the latest version…'}
        {' · '}
        {primary.note}
      </p>
      {primary.id === 'linux' && !loading && (
        <p className="dl-meta">
          {primaryUrl
            ? 'Ubuntu beta: screenshots and hiding the panel from screen sharing are unavailable. Protected overlays and meetings are blocked.'
            : 'The current release does not include an Ubuntu installer yet.'}
        </p>
      )}
      {primary.id === 'windows' && <p className="dl-meta">{OLDER_WINDOWS_NOTE}</p>}

      <ul className="dl-others">
        {others.map((platform) => (
          <li key={platform.id}>
            <a
              href={platform.url || releasesPage}
              rel="noreferrer"
              onClick={platform.id === 'windows' ? warnBeforeWindowsDownload(platform.url) : undefined}
            >
              <Icon name={platform.icon} size={16} />
              <span className="dl-other-label">{platform.label}</span>
              <span className="dl-other-note">{platform.note}</span>
            </a>
            {platform.id === 'linux' && (
              <p className="dl-meta">Ubuntu beta: screenshots and hiding the panel from screen sharing are unavailable. Protected overlays and meetings are blocked.</p>
            )}
            {platform.id === 'windows' && <p className="dl-meta">{OLDER_WINDOWS_NOTE}</p>}
          </li>
        ))}
      </ul>
      {olderWindowsWarning}

      {/*
        Shipped unsigned for now, so both platforms show a warning that
        looks like a malware alert. Saying so plainly costs less than
        the installs lost to people who assume the worst and stop -
        remove this once the Apple and Windows certificates are in
        place. In the modal it is a <details> instead: the person
        clicked a button in an app they already trust, so the warning is
        a reassurance they can open, not the first thing they read.
      */}
      {compact ? (
        <details className="dl-warning dl-warning-compact">
          <summary>
            <Icon name="shield" size={15} />
            <span>{primary?.id === 'linux' ? 'Install on Ubuntu' : "You'll see a warning"}</span>
            <Icon name="chevron-down" size={15} className="dl-warning-chevron" />
          </summary>
          <WarningBody platform={primary} />
        </details>
      ) : (
        <section className="dl-warning">
          <h2>
            <Icon name="shield" size={17} />
            <span>First time opening it</span>
          </h2>
          <WarningBody platform={primary} />
        </section>
      )}
    </div>
  );
}

/**
 * Shown before the Windows installer downloads on Windows 10 older than
 * version 2004. The same explanation as the desktop app's first-run notice
 * (desktop/src/components/WindowsShareNotice.tsx).
 */
function OlderWindowsWarning({ url, onClose }) {
  return (
    <Modal
      title="Before you download: screen sharing on your Windows version"
      size="md"
      onClose={onClose}
      actions={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {/* Closed after the click has started the download. */}
          <a className="dl-primary" href={url} onClick={() => setTimeout(onClose, 0)}>
            <Icon name="download" size={18} />
            <span>Download anyway</span>
          </a>
        </>
      }
    >
      <p>
        This PC appears to run Windows 10 older than version 2004. KrishvaQ works on it, with one
        limit when you share your screen:
      </p>
      <ul>
        <li>
          <strong>Share a window or a tab:</strong> others do not see the KrishvaQ overlay.
        </li>
        <li>
          <strong>Share your entire screen:</strong> others see a dark box where the overlay is.
          What the overlay shows stays private, but the box itself is visible.
        </li>
      </ul>
      <p>
        So in your meeting, choose <strong>Window</strong> or <strong>Tab</strong>, not{' '}
        <strong>Entire screen</strong>. KrishvaQ reminds you about this before every meeting.
      </p>
    </Modal>
  );
}

// The OS versions and architectures a build runs on (PLATFORMS in
// useDesktopRelease.js), one per line under its download button.
function Requirements({ platform }) {
  if (!platform?.requirements) return null;
  return (
    <p className="dl-landing-req">
      {platform.requirements.map((line) => (
        <span key={line}>{line}</span>
      ))}
    </p>
  );
}

function WarningBody({ platform }) {
  if (platform?.id === 'linux') {
    return (
      <>
        <p>Open a terminal in the folder containing your downloaded installer and run:</p>
        <p><code>sudo apt install ./KrishvaQ-VERSION-linux-amd64.deb</code></p>
        <p>Replace VERSION with the downloaded version. Your system may ask for your administrator password.</p>
      </>
    );
  }
  return (
    <>
      <p>
        Your operating system will warn you
        the first time. It&apos;s safe to continue.
      </p>
      <dl>
        <dt>
          <Icon name="windows" size={14} />
          <span>Windows</span>
        </dt>
        <dd>
          On the blue &ldquo;Windows protected your PC&rdquo; screen, choose{' '}
          <strong>More info</strong>, then <strong>Run anyway</strong>.
        </dd>
        <dt>
          <Icon name="apple" size={14} />
          <span>macOS</span>
        </dt>
        <dd>
          Right-click the app and choose <strong>Open</strong>, then{' '}
          <strong>Open</strong> again. Double-clicking alone will be blocked.
        </dd>
      </dl>
    </>
  );
}

export default DesktopDownload;
